import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Loader,
  Message,
  Segment,
  Tab,
  Table,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import {
  contractService,
  ContractInactiveCancellationRunListItemDto,
  ContractInactiveJobSettingsDto,
  ContractInactivePreviewResponseDto,
  ContractRestorationHistoryItemDto,
} from '../../services/contracts/ContractService';
import { formatCnpj } from '../../services/utils/stringUtils';
import { getContractStatusText } from '../../utils/contractStatusUtils';
import { getDefaultButtonStyle, getButtonHoverEffects } from '../../styles/designSystem';

const WEEKDAY_OPTIONS = [
  { key: '0', value: 0, text: 'Dom' },
  { key: '1', value: 1, text: 'Seg' },
  { key: '2', value: 2, text: 'Ter' },
  { key: '3', value: 3, text: 'Qua' },
  { key: '4', value: 4, text: 'Qui' },
  { key: '5', value: 5, text: 'Sex' },
  { key: '6', value: 6, text: 'Sáb' },
];

const formatUtc = (value?: string) =>
  value ? new Date(value).toLocaleString('pt-BR') : '—';

const ContractOperationsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<ContractInactiveJobSettingsDto | null>(null);
  const [preview, setPreview] = useState<ContractInactivePreviewResponseDto | null>(null);
  const [runs, setRuns] = useState<ContractInactiveCancellationRunListItemDto[]>([]);
  const [restorations, setRestorations] = useState<ContractRestorationHistoryItemDto[]>([]);
  const [exportingPreview, setExportingPreview] = useState(false);
  const [downloadingRunId, setDownloadingRunId] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [settingsData, previewData, runsData, restorationsData] = await Promise.all([
        contractService.getInactiveJobSettings(),
        contractService.getInactivePreview(100),
        contractService.getInactiveCancellationRuns(50),
        contractService.getRestorationHistory(200),
      ]);
      setSettings(settingsData);
      setPreview(previewData);
      setRuns(runsData);
      setRestorations(restorationsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar central operacional.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveSettings = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      const updated = await contractService.updateInactiveJobSettings(settings);
      setSettings(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar configuração.');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPreview = async () => {
    try {
      setExportingPreview(true);
      await contractService.downloadInactivePreviewExport(1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao exportar fila de inativos.');
    } finally {
      setExportingPreview(false);
    }
  };

  const handleDownloadRun = async (runId: string) => {
    try {
      setDownloadingRunId(runId);
      await contractService.downloadInactiveRunReport(runId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao baixar laudo.');
    } finally {
      setDownloadingRunId(null);
    }
  };

  const toggleWeekday = (day: number) => {
    if (!settings) return;
    const current = settings.jobRunDaysOfWeek ?? [];
    const next = current.includes(day)
      ? current.filter((d) => d !== day)
      : [...current, day].sort((a, b) => a - b);
    setSettings({ ...settings, jobRunDaysOfWeek: next });
  };

  const configPane = useMemo(
    () => (
      <Segment>
        {!settings ? (
          <Message info>Nenhuma configuração carregada.</Message>
        ) : (
          <Form>
            <Form.Group widths="equal">
              <Form.Field>
                <Checkbox
                  toggle
                  label="Job habilitado"
                  checked={settings.enabled}
                  onChange={(_, data) =>
                    setSettings({ ...settings, enabled: !!data.checked })
                  }
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  toggle
                  label="Enviar relatório por e-mail"
                  checked={settings.sendReportEmailEnabled}
                  onChange={(_, data) =>
                    setSettings({ ...settings, sendReportEmailEnabled: !!data.checked })
                  }
                />
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Cron</label>
                <Input
                  value={settings.cronExpression}
                  onChange={(_, data) =>
                    setSettings({ ...settings, cronExpression: data.value })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Limite de inatividade (horas)</label>
                <Input
                  type="number"
                  value={settings.inactiveThresholdHours}
                  onChange={(_, data) =>
                    setSettings({
                      ...settings,
                      inactiveThresholdHours: Number(data.value) || 72,
                    })
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Fuso horário</label>
                <Input
                  value={settings.timeZone}
                  onChange={(_, data) => setSettings({ ...settings, timeZone: data.value })}
                />
              </Form.Field>
            </Form.Group>
            <Form.Field>
              <label>Dias de execução</label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {WEEKDAY_OPTIONS.map((day) => (
                  <Checkbox
                    key={day.key}
                    label={day.text}
                    checked={(settings.jobRunDaysOfWeek ?? []).includes(day.value)}
                    onChange={() => toggleWeekday(day.value)}
                  />
                ))}
              </div>
            </Form.Field>
            <Form.Field>
              <label>Datas excluídas (YYYY-MM-DD, separadas por vírgula)</label>
              <Input
                value={(settings.excludedDates ?? []).join(', ')}
                onChange={(_, data) =>
                  setSettings({
                    ...settings,
                    excludedDates: data.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
              />
            </Form.Field>
            {settings.updatedAtUtc && (
              <Message size="small" info>
                Última atualização: {formatUtc(settings.updatedAtUtc)}
                {settings.persistedInDatabase ? ' (persistido no banco)' : ''}
              </Message>
            )}
            <Button
              primary
              loading={saving}
              disabled={saving}
              onClick={handleSaveSettings}
              style={getDefaultButtonStyle()}
              {...getButtonHoverEffects('default')}
            >
              <Icon name="save" />
              Salvar configuração
            </Button>
          </Form>
        )}
      </Segment>
    ),
    [settings, saving]
  );

  const previewPane = (
    <Segment>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <Header as="h4" style={{ margin: 0 }}>
          Fila de cancelamento por inatividade
          {preview ? ` (${preview.totalEligible} elegíveis)` : ''}
        </Header>
        <Button
          icon
          labelPosition="left"
          loading={exportingPreview}
          disabled={exportingPreview}
          onClick={handleExportPreview}
        >
          <Icon name="download" />
          Exportar XLSX
        </Button>
      </div>
      {preview && (
        <Message size="small" info>
          Gerado em {formatUtc(preview.generatedAtUtc)} — cutoff {formatUtc(preview.cutoffUtc)}
        </Message>
      )}
      <Table celled compact selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Cliente</Table.HeaderCell>
            <Table.HeaderCell>CNPJ</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Último movimento</Table.HeaderCell>
            <Table.HeaderCell>ICCIDs</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(preview?.items ?? []).map((item) => (
            <Table.Row key={item.contractId}>
              <Table.Cell>{item.clientRazaoSocial ?? '—'}</Table.Cell>
              <Table.Cell>{item.clientCnpj ? formatCnpj(item.clientCnpj) : '—'}</Table.Cell>
              <Table.Cell>
                {item.status != null ? getContractStatusText(item.status) : '—'}
              </Table.Cell>
              <Table.Cell>{formatUtc(item.lastMovementAtUtc)}</Table.Cell>
              <Table.Cell>{item.iccidCount}</Table.Cell>
            </Table.Row>
          ))}
          {(preview?.items ?? []).length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5} textAlign="center">
                Nenhum contrato elegível no momento.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Segment>
  );

  const runsPane = (
    <Segment>
      <Table celled compact selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Executado em</Table.HeaderCell>
            <Table.HeaderCell>Elegíveis</Table.HeaderCell>
            <Table.HeaderCell>Cancelados</Table.HeaderCell>
            <Table.HeaderCell>Falhas</Table.HeaderCell>
            <Table.HeaderCell>ICCID liberados</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Laudo</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {runs.map((run) => (
            <Table.Row key={run.id}>
              <Table.Cell>{formatUtc(run.executedAtUtc)}</Table.Cell>
              <Table.Cell>{run.eligibleContracts}</Table.Cell>
              <Table.Cell>{run.cancelledContracts}</Table.Cell>
              <Table.Cell>{run.failedContracts}</Table.Cell>
              <Table.Cell>{run.liberatedIccids}</Table.Cell>
              <Table.Cell>
                {run.skipped
                  ? `Ignorado: ${run.skipReason ?? '—'}`
                  : run.emailSent
                    ? 'E-mail enviado'
                    : 'Executado'}
              </Table.Cell>
              <Table.Cell>
                {run.hasReport ? (
                  <Button
                    size="mini"
                    icon
                    loading={downloadingRunId === run.id}
                    disabled={downloadingRunId === run.id}
                    onClick={() => handleDownloadRun(run.id)}
                  >
                    <Icon name="download" />
                  </Button>
                ) : (
                  '—'
                )}
              </Table.Cell>
            </Table.Row>
          ))}
          {runs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={7} textAlign="center">
                Nenhuma execução registrada.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Segment>
  );

  const restorationsPane = (
    <Segment>
      <Table celled compact selectable>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Data</Table.HeaderCell>
            <Table.HeaderCell>Cliente</Table.HeaderCell>
            <Table.HeaderCell>Status alvo</Table.HeaderCell>
            <Table.HeaderCell>Resultado</Table.HeaderCell>
            <Table.HeaderCell>Operador</Table.HeaderCell>
            <Table.HeaderCell>Mensagem</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {restorations.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell>{formatUtc(item.createdAtUtc)}</Table.Cell>
              <Table.Cell>
                {item.clientRazaoSocial ?? '—'}
                <br />
                <small>{item.clientCnpj ? formatCnpj(item.clientCnpj) : ''}</small>
              </Table.Cell>
              <Table.Cell>
                {item.targetStatus != null ? getContractStatusText(item.targetStatus) : '—'}
              </Table.Cell>
              <Table.Cell>{item.restored ? 'Restaurado' : 'Falhou'}</Table.Cell>
              <Table.Cell>{item.restoredByUserName ?? '—'}</Table.Cell>
              <Table.Cell>{item.message ?? '—'}</Table.Cell>
            </Table.Row>
          ))}
          {restorations.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={6} textAlign="center">
                Nenhuma restauração registrada.
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Segment>
  );

  const panes = [
    { menuItem: 'Configuração', render: () => <Tab.Pane>{configPane}</Tab.Pane> },
    { menuItem: 'Fila de inativos', render: () => <Tab.Pane>{previewPane}</Tab.Pane> },
    { menuItem: 'Execuções / laudos', render: () => <Tab.Pane>{runsPane}</Tab.Pane> },
    { menuItem: 'Restaurações', render: () => <Tab.Pane>{restorationsPane}</Tab.Pane> },
  ];

  return (
    <GlassPageWrapper title="Central Operacional de Contratos" icon="settings">
      <Grid>
        <Grid.Column width={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <Header as="h3" style={{ margin: 0 }}>
              Parâmetros do job de inativos, fila, laudos e histórico de restaurações
            </Header>
            <Button icon labelPosition="left" onClick={loadAll} loading={loading}>
              <Icon name="refresh" />
              Atualizar
            </Button>
          </div>
          {error && <Message negative content={error} />}
          {loading ? <Loader active inline="centered" /> : <Tab panes={panes} />}
        </Grid.Column>
      </Grid>
    </GlassPageWrapper>
  );
};

export default ContractOperationsPage;
